import { use } from 'react';

import { type ListDropdownItem } from '@sr/types';
import { DropdownContexts } from '../../../../context/DropdownContext';
import { Checkbox } from '../../../../../Checkbox';
import { Chevron } from '../../../../../../Icons';
import { colors } from '../../../../../../../themes';
import { ChevronWrapper, GroupedListContent, GroupedListWrapper, Label, Badge } from './style';

type BadgeConfig = {
  badge: {
    text: string;
    variant: 'success' | 'error' | 'neutral';
  };
};

const hasBadge = (meta: unknown): meta is BadgeConfig => {
  if (typeof meta !== 'object' || meta === null) return false;
  if (!('badge' in meta)) return false;

  const metaObj = meta as Record<string, unknown>;
  const badge = metaObj.badge;

  return typeof badge === 'object' && badge !== null && 'text' in badge && 'variant' in badge;
};

export const ListItemSelectGroup = <T = unknown,>({
  item,
  children,
  nestedIndex = 0,
  checkbox,
}: {
  item: ListDropdownItem<T>;
  children?: React.ReactNode;
  nestedIndex?: number;
  checkbox?: boolean;
}) => {
  const { isChildOpen, toggleOpenChild, toggleOpen } = use(DropdownContexts.useDropdown);
  const { toggleItem, isItemSelected } = use(DropdownContexts.useValues);
  const { searchActive } = use(DropdownContexts.useSearch);
  const { multiSelect } = use(DropdownContexts.props);

  const isDisabled = item.disabled === true;

  const handleClick = () => {
    if (isDisabled) return;

    toggleItem(item);
  };

  const checked = isItemSelected(item);
  const semiChecked = checked === 'partial';
  const shouldToggleChildren = item.children.length > 0;
  const isOpen = isChildOpen(item.value);
  const showAsOpen = isChildOpen(item.value) || searchActive;

  const handleGroupClick = () => {
    if (isDisabled) return;

    const selectItem = () => {
      toggleItem(item);
    };
    const openChild = () => {
      if (!isOpen) {
        toggleOpenChild(item.value);
      }
    };
    const closeDropdown = () => {
      if (!multiSelect) {
        toggleOpen();
      }
    };

    if (!checkbox) {
      if (shouldToggleChildren) {
        openChild();
      } else {
        selectItem();
        closeDropdown();
      }

      return;
    }

    selectItem();
    if (checked !== true) closeDropdown();
  };

  const handleChevronClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    toggleOpenChild(item.value);
  };

  return (
    <GroupedListWrapper onClick={handleGroupClick} nestedIndex={nestedIndex} disabled={isDisabled}>
      {checkbox && (
        <Checkbox
          onChange={handleClick}
          semiChecked={semiChecked}
          checked={semiChecked ? false : checked}
          color={colors.primary.grey}
          disabled={isDisabled}
        />
      )}

      <GroupedListContent>
        <Label showBold={item.type === 'group'}>
          {item.label}
          {hasBadge(item.meta) && (
            <Badge variant={item.meta.badge.variant}>{item.meta.badge.text}</Badge>
          )}
        </Label>

        {children}
      </GroupedListContent>

      {shouldToggleChildren && (
        <ChevronWrapper onClick={handleChevronClick}>
          <Chevron height="24px" width="24px" rotation={showAsOpen ? 180 : 0} />
        </ChevronWrapper>
      )}
    </GroupedListWrapper>
  );
};
